// ── Role-Based Calendar Defaults ──
// Maps each user role to their default event type and department filters.
// Single source of truth — consumed by SchedulingCalendarView.

import type { UserRole } from "@/data/RoleContext";
import type { EventType, ScheduleDepartment } from "./types";

export interface CalendarRoleDefaults {
  /** Default event types shown when user first opens calendar */
  eventTypes: EventType[];
  /** Department filter preset */
  departments: ScheduleDepartment[];
  /** Whether to show "My Schedule" by default */
  myScheduleDefault: boolean;
  /** Label shown in the view preset selector */
  label: string;
}

export const ROLE_CALENDAR_DEFAULTS: Record<UserRole, CalendarRoleDefaults> = {
  super_admin: {
    eventTypes: ["install", "inspection", "followup", "delivery", "consultation", "sales_appointment", "service_call", "pre_install"],
    departments: ["install", "sales", "warehouse", "service", "operations"],
    myScheduleDefault: false,
    label: "All Events",
  },
  owner: {
    eventTypes: ["install", "inspection", "followup", "delivery", "consultation", "sales_appointment", "service_call", "pre_install"],
    departments: ["install", "sales", "warehouse", "service", "operations"],
    myScheduleDefault: false,
    label: "All Events",
  },
  ops_manager: {
    eventTypes: ["install", "inspection", "delivery", "pre_install"],
    departments: ["install", "warehouse"],
    myScheduleDefault: false,
    label: "Install & Ops",
  },
  admin: {
    eventTypes: ["install", "inspection", "delivery", "pre_install", "service_call"],
    departments: ["install", "warehouse", "service"],
    myScheduleDefault: false,
    label: "Operations",
  },
  head_of_installation: {
    eventTypes: ["install", "inspection", "pre_install"],
    departments: ["install"],
    myScheduleDefault: false,
    label: "Install Schedule",
  },
  warehouse_manager: {
    eventTypes: ["delivery", "install"],
    departments: ["warehouse", "install"],
    myScheduleDefault: false,
    label: "Deliveries & Installs",
  },
  installer: {
    eventTypes: ["install", "inspection", "pre_install"],
    departments: ["install"],
    myScheduleDefault: true,
    label: "My Installs",
  },
  subcontractor: {
    eventTypes: ["install", "inspection"],
    departments: ["install"],
    myScheduleDefault: true,
    label: "My Assignments",
  },
  sales: {
    // Include install-side visibility so sales reps can still see live
    // schedule/dispatch data for sold jobs and handoffs.
    eventTypes: ["sales_appointment", "consultation", "followup", "pre_install", "install", "inspection"],
    departments: ["sales", "install"],
    myScheduleDefault: true,
    label: "My Appointments + Installs",
  },
  sales_manager: {
    eventTypes: ["sales_appointment", "consultation", "followup"],
    departments: ["sales"],
    myScheduleDefault: false,
    label: "Sales Schedule",
  },
  call_center: {
    eventTypes: ["sales_appointment", "consultation", "followup"],
    departments: ["sales"],
    myScheduleDefault: false,
    label: "Appointments & Follow-ups",
  },
  cfo: {
    eventTypes: ["install", "sales_appointment"],
    departments: ["install", "sales"],
    myScheduleDefault: false,
    label: "Revenue Events",
  },
  finance_manager: {
    eventTypes: ["install", "sales_appointment"],
    departments: ["install", "sales"],
    myScheduleDefault: false,
    label: "Revenue Events",
  },
  bookkeeper: {
    eventTypes: ["install"],
    departments: ["install"],
    myScheduleDefault: false,
    label: "Completed Installs",
  },
  procurement_manager: {
    eventTypes: ["delivery", "install"],
    departments: ["warehouse", "install"],
    myScheduleDefault: false,
    label: "Deliveries & Installs",
  },
  inventory_manager: {
    eventTypes: ["delivery"],
    departments: ["warehouse"],
    myScheduleDefault: false,
    label: "Deliveries",
  },
  it_staff: {
    eventTypes: ["install", "inspection", "delivery", "pre_install", "service_call"],
    departments: ["install", "warehouse", "service"],
    myScheduleDefault: false,
    label: "Operations",
  },
};
